import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search as SearchIcon, Wifi, WifiOff, Loader2, Activity, User as UserIcon,
  Package, Calendar, HardDrive, Smartphone, Clock, Check, X, Router as RouterIcon,
  RotateCcw, Ban, ShieldCheck,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (sec) => {
  const s = Number(sec || 0);
  if (s <= 0) return '0m';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || (!d && !h)) parts.push(`${m}m`);
  return parts.join(' ');
};

const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / (1024 ** 2)).toFixed(1)} MB`;
  return `${(n / (1024 ** 3)).toFixed(2)} GB`;
};

/**
 * VoucherUsageCheckModal — reusable Cek Pemakaian modal.
 * Props:
 *   open, onOpenChange, initialCode
 *   onSuccess?: () => void  — dipanggil setelah aksi Reset Session / Enable-Disable sukses,
 *                             agar parent (mis. tabel Vouchers) bisa refetch datanya.
 */
export default function VoucherUsageCheckModal({ open, onOpenChange, initialCode, onSuccess }) {
  const { token, user } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(''); // '' | 'reset' | 'toggle'

  // Only admin/super_admin/kepala_cabang can perform actions
  const userRole = (user?.role || '').toLowerCase();
  const canAct = ['admin', 'super_admin', 'superadmin', 'kepala_cabang'].includes(userRole);

  // 2026-07-31: Auto-fill & submit saat modal dibuka dengan initialCode (dari tombol row "Cek Pemakaian")
  useEffect(() => {
    if (open && initialCode) {
      setQuery(initialCode);
      (async () => {
        setLoading(true); setError(''); setResult(null);
        try {
          const res = await axios.get(`${API_URL}/api/vouchers/usage-check`, {
            params: { q: initialCode },
            headers: { Authorization: `Bearer ${token}` },
          });
          setResult(res.data);
        } catch (e) {
          setError(e.response?.data?.detail || e.message || 'Gagal cek pemakaian');
        } finally {
          setLoading(false);
        }
      })();
    } else if (!open) {
      setQuery(''); setResult(null); setError('');
    }
  }, [open, initialCode]);

  const reset = () => { setQuery(''); setResult(null); setError(''); };

  const handleCheck = async () => {
    const q = (query || '').trim();
    if (!q) { toast.error('Masukkan kode voucher atau MAC address'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/vouchers/usage-check`, {
        params: { q },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Gagal cek pemakaian');
    } finally {
      setLoading(false);
    }
  };

  // Refetch current voucher result (used after action success)
  const refetch = async () => {
    const code = result?.voucher?.code;
    if (!code) return;
    try {
      const res = await axios.get(`${API_URL}/api/vouchers/usage-check`, {
        params: { q: code },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
    } catch (_) { /* ignore refetch errors */ }
  };

  const handleResetSession = async () => {
    const code = result?.voucher?.code;
    if (!code) return;
    if (!window.confirm(`Reset semua sesi aktif untuk voucher ${code}?\n\nVoucher tetap aktif — pelanggan bisa login ulang.`)) return;
    setActionLoading('reset');
    try {
      const res = await axios.post(`${API_URL}/api/vouchers/${encodeURIComponent(code)}/reset-session`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Session di-reset');
      await refetch();
      if (typeof onSuccess === 'function') {
        try { onSuccess(); } catch (_) { /* ignore parent refresh errors */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Gagal reset sesi');
    } finally {
      setActionLoading('');
    }
  };

  const handleToggleLogin = async () => {
    const code = result?.voucher?.code;
    if (!code) return;
    const currentlyEnabled = !!result?.radius?.enabled;
    const willEnable = !currentlyEnabled;
    const label = willEnable ? 'AKTIFKAN' : 'NONAKTIFKAN';
    const warn = willEnable
      ? `AKTIFKAN kembali voucher ${code}?\n\nPelanggan bisa login lagi setelah ini.`
      : `NONAKTIFKAN voucher ${code}?\n\nPelanggan TIDAK bisa login sampai admin aktifkan kembali. Sesi aktif akan ditendang.`;
    if (!window.confirm(warn)) return;
    setActionLoading('toggle');
    try {
      const res = await axios.post(
        `${API_URL}/api/vouchers/${encodeURIComponent(code)}/toggle-login`,
        null,
        {
          params: { enable: willEnable },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(res.data?.message || `Voucher ${label}`);
      await refetch();
      if (typeof onSuccess === 'function') {
        try { onSuccess(); } catch (_) { /* ignore parent refresh errors */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `Gagal ${label.toLowerCase()} voucher`);
    } finally {
      setActionLoading('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange?.(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <SearchIcon className="w-5 h-5" /> Cek Pemakaian Voucher
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Kode Voucher | MAC Address</label>
            <div className="flex gap-2 mt-1">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCheck(); }}
                placeholder="Contoh: HM90010 atau AA:BB:CC:11:22:33"
                data-testid="usage-check-input"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
              <Button
                onClick={handleCheck}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-10"
                data-testid="usage-check-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
                <span className="ml-1">Cari</span>
              </Button>
            </div>
            <div className="mt-1 text-[10px] text-rose-600 dark:text-rose-400">
              CATATAN: Pencarian MAC tidak efektif jika perangkat menggunakan MAC randomization (MAC acak).
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/40 px-3 py-2 text-xs text-rose-700 dark:text-rose-300" data-testid="usage-check-error">
              {error}
            </div>
          )}

          {result && result.voucher && (
            <div className="space-y-3" data-testid="usage-check-result">
              <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 p-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">Laporan Pemakaian Voucher</div>
                <div className="text-base font-bold text-emerald-700 dark:text-emerald-300 font-mono">[ {result.voucher.code} ]</div>
                {result.voucher.owner_name && (
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    Owner: <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{result.voucher.owner_name}</span>
                  </div>
                )}
              </div>

              <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold ${
                result.session?.is_online
                  ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
              }`}>
                {result.session?.is_online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                {result.session?.is_online ? 'ONLINE' : 'OFFLINE'}
                {result.voucher.branch_name && (
                  <span className="ml-auto text-[11px] font-normal opacity-80">{result.voucher.branch_name}</span>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                <div className="grid grid-cols-3 px-3 py-2">
                  <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Activity className="w-3 h-3" /> Izin Login</div>
                  <div className="col-span-2">
                    {result.radius?.enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-semibold"><Check className="w-3 h-3" /> Enabled</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-semibold" title={(!result.remaining?.time_unlimited && Number(result.remaining?.seconds_left || 0) <= 0 && result.voucher?.used_at) ? 'Voucher expired — masa aktif habis sejak login pertama' : 'Login diblokir di RADIUS'}><X className="w-3 h-3" /> Disabled{(!result.remaining?.time_unlimited && Number(result.remaining?.seconds_left || 0) <= 0 && result.voucher?.used_at) ? ' (expired)' : ''}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 px-3 py-2">
                  <div className="text-slate-500 dark:text-slate-400">Kode Voucher</div>
                  <div className="col-span-2 font-mono font-semibold text-slate-800 dark:text-slate-100">{result.voucher.code}</div>
                </div>
                <div className="grid grid-cols-3 px-3 py-2">
                  <div className="text-slate-500 dark:text-slate-400">Tipe Service</div>
                  <div className="col-span-2 font-semibold text-slate-800 dark:text-slate-100 uppercase">{result.voucher.package_type || 'HOTSPOT'}</div>
                </div>
                <div className="grid grid-cols-3 px-3 py-2">
                  <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Package className="w-3 h-3" /> Paket</div>
                  <div className="col-span-2 text-slate-800 dark:text-slate-100">
                    <div className="font-semibold">{result.voucher.package_name}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      <span>Speed: {result.voucher.speed_down || '-'}/{result.voucher.speed_up || '-'}</span>
                      <span>Kuota: {result.remaining?.quota_unlimited ? 'Unlimited' : `${result.voucher.quota_gb} GB`}</span>
                      <span>Masa: {result.voucher.validity_days} {result.voucher.validity_unit}</span>
                    </div>
                  </div>
                </div>
                {result.voucher.used_at && (
                  <div className="grid grid-cols-3 px-3 py-2">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Mulai Aktif</div>
                    <div className="col-span-2 font-mono text-slate-800 dark:text-slate-100">{result.voucher.used_at}</div>
                  </div>
                )}
                {result.voucher.expires_at && (
                  <div className="grid grid-cols-3 px-3 py-2">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Jatuh Tempo</div>
                    <div className="col-span-2 font-mono text-slate-800 dark:text-slate-100">{result.voucher.expires_at}</div>
                  </div>
                )}
                <div className="grid grid-cols-3 px-3 py-2 bg-blue-50/50 dark:bg-blue-950/20">
                  <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><HardDrive className="w-3 h-3" /> Data Session</div>
                  <div className="col-span-2 text-slate-800 dark:text-slate-100 space-y-0.5">
                    <div><span className="text-[10px] text-slate-500 dark:text-slate-400">Online Time:</span> <span className="font-semibold text-blue-600 dark:text-blue-300">{fmtDuration(result.session?.total_online_seconds)}</span></div>
                    <div><span className="text-[10px] text-slate-500 dark:text-slate-400">Kuota Terpakai:</span> <span className="font-semibold text-blue-600 dark:text-blue-300">{fmtBytes(result.session?.total_bytes_used)}</span></div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">Sesi: {result.session?.sessions_count} kali</div>
                  </div>
                </div>
                {result.session?.first_session_at && (
                  <div className="grid grid-cols-3 px-3 py-2" data-testid="voucher-session-start">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Waktu Mulai</div>
                    <div className="col-span-2 font-mono text-slate-800 dark:text-slate-100" title="Login pertama voucher ini (MIN(acctstarttime) dari radacct)">{result.session.first_session_at}</div>
                  </div>
                )}
                {result.session?.last_session_at && (
                  <div className="grid grid-cols-3 px-3 py-2" data-testid="voucher-session-end">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Waktu Berakhir</div>
                    <div className="col-span-2 font-mono text-slate-800 dark:text-slate-100" title="Login terakhir voucher ini (MAX(acctstarttime) dari radacct)">{result.session.last_session_at}</div>
                  </div>
                )}
                {/* VOUCHER_EXPIRED_UI_FIX_V1 — jangan tampilkan Sisa Online Efektif kalau wall-clock sudah lewat */}
                {(() => {
                  const isExpired = !result.remaining?.time_unlimited && Number(result.remaining?.seconds_left || 0) <= 0 && !!result.voucher?.used_at;
                  return (
                <div className={"grid grid-cols-3 px-3 py-2 " + (isExpired ? "bg-rose-50/60 dark:bg-rose-950/20" : "bg-emerald-50/50 dark:bg-emerald-950/20")}>
                  <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Session Tersisa</div>
                  <div className="col-span-2 text-slate-800 dark:text-slate-100 space-y-0.5">
                    {isExpired ? (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-bold text-[11px]" title="Wall-clock masa aktif voucher sudah lewat sejak login pertama">
                          <X className="w-3 h-3" /> KADALUARSA (Masa aktif habis)
                        </span>
                      </div>
                    ) : (
                      <>
                        <div><span className="text-[10px] text-slate-500 dark:text-slate-400">Kadaluarsa:</span> <span className="font-semibold text-emerald-600 dark:text-emerald-300" title="Waktu wall-clock sejak login pertama — tetap berjalan meski logout">{result.remaining?.time_unlimited ? 'Unlimited' : fmtDuration(result.remaining?.seconds_left)}</span></div>
                        {result.remaining?.online_seconds_left != null && !result.remaining?.time_unlimited && (
                          <div>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">Sisa Online Efektif:</span>
                            <span className="font-semibold text-teal-600 dark:text-teal-300 ml-1" title="Sisa waktu berdasarkan akumulasi online — timer stop saat logout">
                              {fmtDuration(result.remaining.online_seconds_left)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    <div><span className="text-[10px] text-slate-500 dark:text-slate-400">Kuota:</span> <span className={"font-semibold ml-1 " + (isExpired ? 'text-slate-400 dark:text-slate-500 line-through' : 'text-emerald-600 dark:text-emerald-300')}>{result.remaining?.quota_unlimited ? 'Unlimited' : fmtBytes(result.remaining?.bytes_left)}</span></div>
                  </div>
                </div>
                );})()}
                {(result.session?.router_name || result.session?.current_nas) && (
                  <div className="grid grid-cols-3 px-3 py-2 bg-purple-50/40 dark:bg-purple-950/20">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><RouterIcon className="w-3 h-3" /> Lokasi Online</div>
                    <div className="col-span-2 text-slate-800 dark:text-slate-100">
                      <div className="font-semibold text-purple-700 dark:text-purple-300">{result.session.router_name || 'Router'}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">NAS: {result.session.current_nas}</div>
                    </div>
                  </div>
                )}
                {result.session?.current_mac && (
                  <div className="grid grid-cols-3 px-3 py-2">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><Smartphone className="w-3 h-3" /> Perangkat (MAC)</div>
                    <div className="col-span-2 font-mono font-semibold text-indigo-600 dark:text-indigo-300">{result.session.current_mac}</div>
                  </div>
                )}
                {result.session?.current_ip && (
                  <div className="grid grid-cols-3 px-3 py-2">
                    <div className="text-slate-500 dark:text-slate-400">IP Address</div>
                    <div className="col-span-2 font-mono text-slate-800 dark:text-slate-100">{result.session.current_ip}</div>
                  </div>
                )}
                {result.voucher.agent_name && (
                  <div className="grid grid-cols-3 px-3 py-2">
                    <div className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><UserIcon className="w-3 h-3" /> Agen</div>
                    <div className="col-span-2 text-purple-700 dark:text-purple-300 font-semibold">{result.voucher.agent_name}</div>
                  </div>
                )}
              </div>

              {/* ACTION BAR — admin/kepala_cabang only */}
              {canAct && (
                <div className="rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="voucher-action-bar">
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Aksi Admin</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleResetSession}
                      disabled={!!actionLoading}
                      data-testid="voucher-reset-session-btn"
                      title="Tutup semua sesi aktif — voucher tetap bisa dipakai, pelanggan bisa login ulang"
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 flex items-center justify-center gap-1.5"
                    >
                      {actionLoading === 'reset' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      Reset Session
                    </Button>
                    {result.radius?.enabled ? (
                      <Button
                        onClick={handleToggleLogin}
                        disabled={!!actionLoading}
                        data-testid="voucher-disable-btn"
                        title="Non-aktifkan voucher — pelanggan TIDAK bisa login sampai admin aktifkan lagi"
                        className="bg-rose-600 hover:bg-rose-700 text-white text-xs h-9 flex items-center justify-center gap-1.5"
                      >
                        {actionLoading === 'toggle' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                        Disable Voucher
                      </Button>
                    ) : (
                      <Button
                        onClick={handleToggleLogin}
                        disabled={!!actionLoading}
                        data-testid="voucher-enable-btn"
                        title="Aktifkan kembali voucher — pelanggan bisa login lagi"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 flex items-center justify-center gap-1.5"
                      >
                        {actionLoading === 'toggle' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        Enable Voucher
                      </Button>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug pt-1">
                    <span className="font-semibold text-blue-700 dark:text-blue-300">Reset Session:</span> Tutup sesi stuck (ghost session) — voucher tetap aktif.
                    <br/>
                    <span className={result.radius?.enabled ? "font-semibold text-rose-700 dark:text-rose-300" : "font-semibold text-emerald-700 dark:text-emerald-300"}>
                      {result.radius?.enabled ? 'Disable:' : 'Enable:'}
                    </span> {result.radius?.enabled ? 'Kunci voucher — login akan ditolak.' : 'Buka kunci — pelanggan bisa login lagi.'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
